import { forwardRef, memo, useEffect, useRef } from 'react';
import classNames from 'classnames';
import CountMark from '@lib/components/CountMark/CountMark';
import { ActiveItem, TabBarItem as TabBarItemType } from './constants';
import styles from './TabBar.module.scss';
import { isTabActive } from './utils/activeStateComparison';

type Props = {
  active?: ActiveItem;
  isVisible: boolean;
  item: TabBarItemType;
  onActiveChange: (item: TabBarItemType) => void;
  useScrollIntoView?: boolean;
  isFullWidth?: boolean;
};

const TabBarItem = forwardRef<HTMLButtonElement, Props>(
  (props: Props, forwardedRef) => {
    const {
      active,
      isVisible,
      item,
      onActiveChange,
      useScrollIntoView,
      isFullWidth,
    } = props;
    const { label, counter, counterType, disabled, hasError, value, type } =
      item;
    const isActive = isTabActive(value, active);

    const internalRef = useRef<HTMLButtonElement | null>(null);

    // Combine internal ref with forwarded ref from CroppedRow
    const setRefs = (node: HTMLButtonElement | null) => {
      internalRef.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        // eslint-disable-next-line no-param-reassign
        forwardedRef.current = node;
      }
    };

    useEffect(() => {
      if (useScrollIntoView && isActive && internalRef.current) {
        internalRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'end',
        });
      }
    }, [isActive, useScrollIntoView]);

    return (
      <button
        type="button"
        key={value}
        onClick={() => !disabled && isVisible && onActiveChange(item)}
        data-id={value}
        data-test-id={`tab-bar__tab-item-${value}`}
        className={classNames(
          styles.item,
          type && styles[type],
          {
            [styles.active]: isActive,
            [styles.disabled]: disabled,
            [styles.hasError]: hasError,
            [styles.hasLabel]: !!label,
            [styles.hidden]: !isVisible,
            [styles.fullWidthItem]: isFullWidth,
          },
          'TabBarItem',
        )}
        ref={setRefs}
      >
        {label && <span>{label}</span>}
        {counter !== undefined && (
          <CountMark value={counter} disabled={!isActive} type={counterType} />
        )}
      </button>
    );
  },
);

export default memo(TabBarItem);
